import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import {
    _setApiUrl,
    DEFAULT_AUCTIONS_PER_PAGE
} from './utils/gateways';
import {
    setFeature,
    FEATURE__REFINE,
    FEATURE__LOGGING,
    FEATURE__SORT_AUCTIONEERS,
    FEATURE__HIGHLIGHT_PREMIUM,
    FEATURE__SHOWCASE,
    FEATURE__BROWSER_ROUTER,
    FEATURE__AUCTIONEERS_ONLY
} from './utils/features';
import {attachCssForTheme} from './utils/theme';
import {verifyInt} from './utils/types';
import {_setFullWidgetUrl} from './components/FullWidgetLink';
import { selectOptionsForSite } from './selectOptionsForSite';

import './index.css';

[
    'auctionlook-widget',
    'auctionlook-widget-2',
].forEach(function(domId, i) {
    const rootNode = document.getElementById(domId);
    
    if (!rootNode) {
        if (i === 0)
            console.error('You must have an HTML element with ID "auctionlook-widget" to use the Auctionlook widget');
        return;
    }

    const root = createRoot(rootNode);

    let options = {};

    if (process.env.REACT_APP_INIT_JSON) {
        options = selectOptionsForSite();
        // console.log('initializing from JSON', options);

        if (!options) {
            root.render(
                <div>Contact Auctionlook to enable this script for your site.</div>
            );
            return;
        }
    } else {
        // console.log('initializing from DOM');
        const auctionsPerPage = rootNode.getAttribute('data-auctionsPerPage');
        options = {
            showcase: rootNode.getAttribute('data-showcase'),
            associationState: rootNode.getAttribute('data-associationState') || '',
            auctioneer: rootNode.getAttribute('data-auctioneer'),
            apiUrl: rootNode.getAttribute('data-apiUrl'),
            theme: rootNode.getAttribute('data-theme'),
            useRefine: rootNode.getAttribute('data-useRefine'),
            sortAuctioneers: rootNode.getAttribute('data-sortAuctioneers'),
            debugAttr: rootNode.getAttribute('data-debug'),
            premiumFilter: rootNode.getAttribute('data-premiumFilter') || null,
            highlightPremium: rootNode.getAttribute('data-highlightPremium'),
            fullWidgetUrl: rootNode.getAttribute('data-fullWidgetUrl'),
            routerBasePath: rootNode.getAttribute('data-routerBasePath'),
            lockCategory: rootNode.getAttribute('data-lockCategory'),
            auctionsPerPage: (
                auctionsPerPage
                    ? verifyInt(auctionsPerPage, 'auctionsPerPage')
                    : DEFAULT_AUCTIONS_PER_PAGE
            ),
            auctioneersOnly: rootNode.getAttribute('data-auctioneersOnly'),
        };
    }

    if (i > 0 && options.showcase !== 'true') {
        console.error('Secondary widget must have data-showcase="true"')
        return;
    }

    if (options.apiUrl) {
        _setApiUrl(options.apiUrl);
    }
    if (options.fullWidgetUrl) {
        _setFullWidgetUrl(options.fullWidgetUrl);
    }
    setFeature(FEATURE__LOGGING, options.debugAttr === 'true' || options.debugAttr === '1');
    setFeature(FEATURE__REFINE, options.useRefine === 'true');
    setFeature(FEATURE__SORT_AUCTIONEERS, options.sortAuctioneers === 'true');
    setFeature(FEATURE__HIGHLIGHT_PREMIUM, options.highlightPremium === 'true');
    setFeature(FEATURE__SHOWCASE, options.showcase === 'true');
    setFeature(FEATURE__BROWSER_ROUTER, !!options.routerBasePath);
    setFeature(FEATURE__AUCTIONEERS_ONLY, options.auctioneersOnly === 'true');
    
    attachCssForTheme(options.theme);
    
    root.render(
        <App
            associationState={options.associationState}
            premiumFilter={options.premiumFilter}
            auctioneer={options.auctioneer}
            theme={options.theme}
            auctionsPerPage={options.auctionsPerPage}
            lockCategory={options.lockCategory}
            routerBasePath={options.routerBasePath}
        />
    );
});
